import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/common/Modal';
import { Button, Label, LABEL_TYPE, Spinner, ZeroState } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE, TRIBUTE_VIDEO } from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetType, PERMISSION_TYPE, TributeVideoExportStatus } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { groupBy } from '@/helpers';
import { AssetsContainer } from '@/pages/Event/Asset/AssetsContainer';
import { TributeVideoIcon, VideoDesktopIcon as VideoIcon } from '@/resources';
import { useGetEventByIdQuery, useGetEventTags, useGetEventTributeVideos } from '@/services';
import { Asset, AssetSlot, Tag } from '@/types';

type TributeVideosProps = {
  id: string;
  isMobileView?: boolean;
  organizationPermissions?: string[];
};

export const TributeVideosComponent = ({
  id,
  isMobileView = false,
  organizationPermissions: eventOrganizationPermissions
}: TributeVideosProps) => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);
  const hasTributeVideoCreationAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );

  const { data: eventData, isLoading: isLoadingEvent } = useGetEventByIdQuery(Number(id));
  const { data: allTags } = useGetEventTags(id);

  const tributeVideoTags =
    allTags?.data
      ?.filter((tag: Tag) => tag.name === TRIBUTE_VIDEO)
      .map((tag: Tag) => `${tag.id}`) || [];

  const { data: allAssets } = useGetEventTributeVideos(
    id,
    PAGE_SIZE,
    hasOrgAccess ? [] : tributeVideoTags.length === 0 ? ['-1'] : tributeVideoTags,
    undefined,
    hasTributeVideoCreationAccess,
    undefined,
    undefined,
    window.TRIBUTE_VIDEOS_REFETCH_INTERVAL
  );

  const completedVideos = allAssets?.pages.flatMap((page) =>
    page.data?.items.filter((item: Asset) => item.status === TributeVideoExportStatus.Completed)
  );

  const handleCreateTributeVideos = () => {
    hasOrgAccess
      ? navigate(`/events/${id}/create-video`)
      : navigate(`/events/${id}/upload-content/tribute-videos/create`);
  };

  const handleEditTributeVideo = (selectedAsset: Asset) => {
    hasOrgAccess
      ? navigate(`/events/${id}/create-video/${selectedAsset.id}`)
      : navigate(`/events/${id}/upload-content/tribute-videos/create/${selectedAsset.id}`);
  };

  const groupedAssetSlots = groupBy(
    eventData?.data.assetSlots,
    ({ slotType }: AssetSlot) => slotType?.assetType
  );
  const assets = allAssets?.pages.flatMap((page) => page.data?.items);

  const handleDisable = () => {
    return !!(Number(assets?.length) >= Number(groupedAssetSlots.get(TRIBUTE_VIDEO)?.length));
  };

  return (
    <>
      {isLoadingEvent ? (
        <div className='flex min-h-[80vh] items-center justify-center'>
          <Spinner />
        </div>
      ) : (
        <div
          className={`flex flex-col ${
            hasOrgAccess ? 'w-full' : 'max-w-[960px] md:min-w-full lg:min-w-[960px]'
          } h-full ${hasTributeVideoCreationAccess && hasContentManagementAccess ? '' : 'relative'}`}>
          <div className={`mt-4 flex flex-col md:mt-9 ${hasOrgAccess && 'md:mr-20'}`}>
            {isMobileView && (
              <>
                <div
                  className={`flex w-full flex-row items-center justify-between pb-4 ${
                    !hasOrgAccess && 'mb-4 border-b border-secondary-blue-gray'
                  }`}>
                  <Label
                    type={LABEL_TYPE.H3_BOLD}
                    text={t('tributeVideos')}
                    className='w-full font-sans text-primary-blue'
                  />
                  <div className='z-10 inline-flex w-full flex-col items-end gap-2.5 px-2'>
                    <div className='font-sans text-xl font-semibold text-primary-blue'>
                      {`${assets?.length ?? 0}/${groupedAssetSlots.get(TRIBUTE_VIDEO)?.length}`}
                    </div>
                  </div>
                </div>
                <Label
                  type={LABEL_TYPE.PARAGRAPH2}
                  text={t('pleaseCreateTributeVideo', {
                    x: groupedAssetSlots.get(TRIBUTE_VIDEO)?.length
                  })}
                  className='pb-6 font-sans text-accent-dark-gray'
                />
              </>
            )}
            {hasOrgAccess && (
              <div className='flex md:justify-end'>
                <Button
                  variant={ButtonVariant.SECONDARY}
                  handleOnClick={handleCreateTributeVideos}
                  disabled={isMobileView && handleDisable()}
                  className={`w-full md:w-fit ${isMobileView && handleDisable() && 'text-secondary-steel-blue'}`}>
                  <VideoIcon
                    className={`mr-2.5 md:size-4 ${isMobileView && handleDisable() && 'opacity-60'}`}
                  />
                  {t('createVideo')}
                </Button>
              </div>
            )}
          </div>
          {allAssets?.pages.flatMap((page) => page.data?.items).length === 0 ? (
            <div className='mt-16 flex items-center justify-center md:mt-0 md:h-full'>
              <ZeroState
                icon={
                  <TributeVideoIcon
                    fill='var(--color-secondary-steel-blue)'
                    className='size-[30px]'
                  />
                }
                titleFirstLine={t('createTributeVideosTitleOne')}
                titleSecondLine={t('createTributeVideosTitleTwo')}
                onIconClick={handleCreateTributeVideos}
              />
            </div>
          ) : (
            <div
              className={`h-full max-h-[75%] md:max-h-full ${
                !hasOrgAccess && 'max-w-[960px] rounded-md border border-secondary-blue-gray p-2.5'
              }`}>
              <AssetsContainer
                eventId={String(id)}
                justContent={true}
                assetType={AssetType.TRIBUTE_VIDEO.toString()}
                canBeEdited={true}
                completedVideos={completedVideos}
                className={`${hasOrgAccess ? 'mt-6 md:mt-9 md:!px-20' : '!mt-0 md:!px-0'}`}
                editSelectedAsset={handleEditTributeVideo}
                isScrollable
                assetContainerStyles='md:h-[52vh] 2xl:h-[58vh] 3xl:h-[63vh] 4xl:h-[63vh]'
              />
            </div>
          )}
          {!hasOrgAccess && (
            <div className='mt-4 flex md:justify-end'>
              <Button
                variant={ButtonVariant.SECONDARY}
                handleOnClick={handleCreateTributeVideos}
                disabled={isMobileView && handleDisable()}
                className={`'w-1/2 md:w-fit' ${isMobileView && handleDisable() && 'text-secondary-steel-blue'}`}>
                <VideoIcon
                  className={`mr-2.5 ${isMobileView && handleDisable() && 'opacity-60'}`}
                />
                {t('createVideo')}
              </Button>
            </div>
          )}
          {(!hasTributeVideoCreationAccess || !hasContentManagementAccess) && (
            <div className='absolute inset-0 z-10 backdrop-blur-sm'>
              <Modal
                isOpen={!hasTributeVideoCreationAccess || !hasContentManagementAccess}
                modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                hasCloseIcon={false}
                onClose={() => {
                  undefined;
                }}>
                <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                  <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    text={t('yourOranizationDoesntHavePermission', {
                      permission: hasTributeVideoCreationAccess
                        ? hasContentManagementAccess
                          ? ''
                          : t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`)
                        : t(`${ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION}`)
                    })}
                  />
                </div>
              </Modal>
            </div>
          )}
        </div>
      )}
    </>
  );
};
